import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import {
  ViewportGuidanceDialogComponent,
  ViewportGuidanceDialogResult,
} from '../viewport-guidance/viewport-guidance-dialog.component';

@Injectable({ providedIn: 'root' })
export class ViewportGuidanceService implements OnDestroy {
  private readonly snoozeUntilKey = 'forlab_viewport_guidance_snooze_until';
  private readonly minComfortableWidth = 1024;
  private readonly minComfortableHeight = 700;
  private readonly veryCrampedWidth = 900;
  private readonly veryCrampedHeight = 620;

  private resizeSub?: Subscription;
  private dialogRef?: MatDialogRef<
    ViewportGuidanceDialogComponent,
    ViewportGuidanceDialogResult
  > | null;
  private currentRoute = '';

  constructor(
    private readonly dialog: MatDialog,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  start(): void {
    if (this.resizeSub || typeof window === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.resizeSub = merge(
        timer(650),
        fromEvent(window, 'resize'),
        fromEvent(window, 'orientationchange')
      )
        .pipe(auditTime(350))
        .subscribe(() => this.zone.run(() => this.evaluate()));
    });
  }

  onRouteChanged(url: string): void {
    this.currentRoute = url || '';
    this.evaluate();
  }

  checkNow(): void {
    this.evaluate();
  }

  stop(): void {
    this.resizeSub?.unsubscribe();
    this.resizeSub = undefined;
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private evaluate(): void {
    const isMobileDevice = this.isMobileDevice();
    if (typeof window === 'undefined' || this.shouldSkipRoute() || isMobileDevice || this.isDismissed()) {
      if (isMobileDevice && this.dialogRef) {
        this.dialogRef.close();
      }
      return;
    }

    const width = window.innerWidth || this.document.documentElement.clientWidth || 0;
    const height = window.innerHeight || this.document.documentElement.clientHeight || 0;

    if (!this.isCramped(width, height) || this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialog.open(ViewportGuidanceDialogComponent, {
      width: 'min(700px, calc(100vw - 72px))',
      maxWidth: 'calc(100vw - 72px)',
      maxHeight: 'calc(100dvh - 48px)',
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'viewport-guidance-dialog-panel',
      data: {
        width,
        height,
        isVeryCramped:
          width < this.veryCrampedWidth || height < this.veryCrampedHeight,
      },
    });

    this.dialogRef.afterClosed().subscribe((result) => {
      this.dialogRef = null;
      if (result === 'snooze') {
        this.snoozeToday();
      }
    });
  }

  private isCramped(width: number, height: number): boolean {
    return width < this.minComfortableWidth || height < this.minComfortableHeight;
  }

  private isMobileDevice(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }

    const userAgent = navigator.userAgent || '';
    const isMobileUserAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches === true;
    const hasTouch = Number(navigator.maxTouchPoints || 0) > 0;
    const viewportWidth = window.innerWidth || this.document.documentElement.clientWidth || 0;

    return isMobileUserAgent || (hasCoarsePointer && hasTouch && viewportWidth <= 1180);
  }

  private shouldSkipRoute(): boolean {
    const route = this.normalizeRoute(
      this.currentRoute || window.location.hash.replace(/^#/, '') || window.location.pathname
    );
    return (
      route === '' ||
      route === '/' ||
      route === '/index' ||
      route === '/forlab' ||
      route === '/forlab/index' ||
      route.startsWith('/authentication')
    );
  }

  private normalizeRoute(route: string): string {
    const withoutHashPrefix = (route || '').replace(/^#/, '');
    return withoutHashPrefix.split('?')[0].split('#')[0];
  }

  private isDismissed(): boolean {
    try {
      const snoozeUntil = Number(localStorage.getItem(this.snoozeUntilKey) || '0');
      return Number.isFinite(snoozeUntil) && snoozeUntil > Date.now();
    } catch {
      return false;
    }
  }

  private snoozeToday(): void {
    try {
      const oneDayMs = 24 * 60 * 60 * 1000;
      localStorage.setItem(this.snoozeUntilKey, String(Date.now() + oneDayMs));
    } catch {
      // If persistent storage is unavailable, fall back to the in-memory dialog guard.
    }
  }
}
